/*eslint complexity: ["error", 14]*/
import type { Element } from '@xmpp/xml'
import { BaseBlock, ParsedPayloadIn, Room } from '../../chat-data/types'
import {
  getArchivedFlag,
  getDelayStanza,
  getDelayStanzaStamp,
  getFinInboxStanza,
  getForwardedStanza,
  getInboxStanza,
  getInboxStanzaUnread,
  getMessageBodyStanza,
  getMessageBodyStanzaText,
  getMessageStanza,
  getMessageStanzaData,
  getMuteFlag,
  getQueryStanza,
} from '../helpers'
import { TypedEnvelope } from '../types'
import { getRoomAndUserId } from './getRoomAndUserId'
import { parseMessageBody } from './groupchatStanza'

export const parseFinInboxStanza = (
  stanza: Element
): { lastId: string; queryId: string } | undefined => {
  const fin = getFinInboxStanza(stanza)
  if (!fin) {
    return
  }
  const queryId = stanza?.attr('id')
  const finSet = fin?.getChild('set')
  const last = finSet?.getChild('last')
  return {
    queryId,
    lastId: String(last?.children[0]),
  }
}

export const parseInboxStanza = (stanza: Element) => {
  const inboxStanza = getInboxStanza(stanza)
  if (!inboxStanza) {
    return
  }
  const forwardedStanza = getForwardedStanza(inboxStanza)
  const latestMessageStanza = getMessageStanza(forwardedStanza)
  const queryId = inboxStanza?.attr('queryid') as string | undefined

  const unread = getInboxStanzaUnread(inboxStanza)
  const timestamp = getDelayStanzaStamp(getDelayStanza(forwardedStanza))
  const {
    id: lastMessageId,
    to: lastMessageTo,
    from: lastMessageFrom,
  } = getMessageStanzaData(latestMessageStanza)
  const lastMessageInfo = lastMessageTo?.includes('/') ? lastMessageTo : lastMessageFrom
  const lastMessageBodyStanza = getMessageBodyStanza(latestMessageStanza)
  const lastMessageBody = getMessageBodyStanzaText(lastMessageBodyStanza)

  if (!lastMessageInfo) {
    return
  }

  const { roomId, userId } = getRoomAndUserId(lastMessageInfo)

  if (!roomId || !timestamp || !userId || !lastMessageId) {
    return
  }

  const closed = getArchivedFlag(inboxStanza)

  const room: Room = {
    id: roomId,
    queryId,
    status: closed ? 'CLOSED' : 'OPEN',
    unreadEvents: { count: parseInt(unread ?? '0'), timestamp },
    isMuted: getMuteFlag(inboxStanza),
  }

  const lastEventBase = {
    id: lastMessageId,
    timestamp: timestamp,
    sent: true,
    author: userId,
    roomId,
    statuses: {},
  } satisfies BaseBlock

  try {
    const parsedBody = parseMessageBody(lastMessageBody)
    const lastEvent = envelopeToParsedPayloadIn(lastEventBase, parsedBody)
    room.lastEvents = lastEvent ? [lastEvent] : []
    room.unreadEvents.timestamp = timestamp
    return room
  } catch (e) {
    // TODO https://floatingapps.atlassian.net/browse/CPP-9717
    return
  }
}

const envelopeToParsedPayloadIn = (
  eventBase: BaseBlock,
  envelope: TypedEnvelope | null
): ParsedPayloadIn | undefined => {
  if (!envelope || !('payload' in envelope)) {
    return undefined
  }
  return {
    ...eventBase,
    ...envelope.payload,
  }
}

export const parseInboxItemStanza = (stanza: Element) => {
  const queryStanza = getQueryStanza(stanza)
  const forwardedStanza = getForwardedStanza(queryStanza)
  const latestMessageStanza = getMessageStanza(forwardedStanza)

  const { to: lastMessageTo, from: lastMessageFrom } = getMessageStanzaData(latestMessageStanza)
  const lastMessageInfo = lastMessageTo?.includes('/') ? lastMessageTo : lastMessageFrom

  if (!lastMessageInfo) {
    return
  }

  const { roomId, userId } = getRoomAndUserId(lastMessageInfo)

  if (!roomId || !userId) {
    return
  }
  const closed = getArchivedFlag(queryStanza)
  const isMuted = getMuteFlag(queryStanza)
  return {
    id: roomId,
    status: closed ? 'CLOSED' : 'OPEN',
    isMuted,
  } as const
}
